import { supabase } from './supabase';

const BUCKET_NAME = 'thumbnails';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://bwrjzklcyhddlpzbjlgd.supabase.co';

/**
 * Verifica se uma URL é externa (não do Supabase)
 */
export function isExternalUrl(url: string): boolean {
  if (!url) return false;
  return !url.includes('supabase.co') && !url.includes('supabase.storage') && !url.includes('supabase');
}

/**
 * Baixa uma imagem ou vídeo de uma URL externa
 */
async function downloadMedia(url: string): Promise<Blob> {
  try {
    console.log('   🔗 Fazendo requisição para:', url);
    
    const isVideo = url.toLowerCase().endsWith('.mp4');
    const acceptHeader = isVideo ? 'video/*' : 'image/*';
    
    const response = await fetch(url, {
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Accept': acceptHeader
      }
    });

    console.log('   📡 Status da resposta:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Sem detalhes');
      throw new Error(`Falha ao baixar mídia: ${response.status} ${response.statusText}. Detalhes: ${errorText.substring(0, 200)}`);
    }

    const blob = await response.blob();
    console.log('   ✅ Blob criado. Tipo:', blob.type, 'Tamanho:', (blob.size / 1024).toFixed(2), 'KB');
    
    return blob;
  } catch (error: any) {
    console.error('   ❌ Erro no download:', error);
    if (error.message?.includes('CORS')) {
      throw new Error(`Erro CORS: A mídia não pode ser baixada devido a políticas de CORS. URL: ${url}`);
    }
    throw new Error(`Erro ao baixar mídia: ${error.message || 'Erro desconhecido'}`);
  }
}

/**
 * Detecta o tipo MIME da imagem ou vídeo
 * IMPORTANTE: Sempre retorna um tipo válido, nunca application/json
 * Suporta: JPEG, PNG, GIF (incluindo GIFs animados), WebP, MP4
 */
function detectMimeType(blob: Blob, url: string): string {
  // Verificar se é vídeo MP4 pela extensão
  if (url.toLowerCase().endsWith('.mp4')) {
    console.log('   📋 Tipo MIME detectado pela extensão: video/mp4');
    console.log('   🎬 Vídeo MP4 detectado');
    return 'video/mp4';
  }

  // Se o blob já tem tipo MIME válido de imagem, usar (exceto se for application/json)
  if (blob.type && blob.type.startsWith('image/') && blob.type !== 'application/json') {
    console.log('   📋 Tipo MIME do blob:', blob.type);
    // Verificar se é GIF animado (mesmo tipo MIME, mas pode ter sido detectado incorretamente)
    if (blob.type === 'image/gif') {
      console.log('   🎬 GIF detectado (pode ser animado)');
    }
    return blob.type;
  }

  // Se o blob tem tipo MIME de vídeo, usar
  if (blob.type && blob.type.startsWith('video/')) {
    console.log('   📋 Tipo MIME do blob (vídeo):', blob.type);
    return blob.type;
  }

  // Fallback pela extensão da URL (mais confiável, especialmente para GIFs animados)
  const ext = url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|mp4)$/)?.[1];
  if (ext === 'jpg' || ext === 'jpeg') {
    console.log('   📋 Tipo MIME detectado pela extensão: image/jpeg');
    return 'image/jpeg';
  }
  if (ext === 'png') {
    console.log('   📋 Tipo MIME detectado pela extensão: image/png');
    return 'image/png';
  }
  if (ext === 'gif') {
    console.log('   📋 Tipo MIME detectado pela extensão: image/gif');
    console.log('   🎬 Suportando GIF (incluindo GIFs animados)');
    return 'image/gif';
  }
  if (ext === 'webp') {
    console.log('   📋 Tipo MIME detectado pela extensão: image/webp');
    return 'image/webp';
  }
  if (ext === 'mp4') {
    console.log('   📋 Tipo MIME detectado pela extensão: video/mp4');
    return 'video/mp4';
  }
  
  // Default seguro: sempre retornar image/jpeg, nunca application/json
  console.warn('   ⚠️ Tipo MIME não detectado, usando padrão: image/jpeg');
  return 'image/jpeg';
}

/**
 * Gera nome único para o arquivo
 */
function generateFileName(cardId: string, originalUrl: string): string {
  // Suporta: jpg, jpeg, png, gif (incluindo GIFs animados), webp, mp4
  const extension = originalUrl.match(/\.(jpg|jpeg|png|gif|webp|mp4)$/i)?.[0] || '.jpg';
  return `cards/${cardId}${extension}`;
}

/**
 * Faz upload de uma imagem ou vídeo para o Supabase Storage
 * Se a URL já for do Supabase, retorna a URL original
 * Se for externa, baixa e faz upload
 */
export async function uploadThumbnailToSupabase(
  thumbnailUrl: string,
  cardId: string
): Promise<string> {
  // Se já está no Supabase, retornar a URL original
  if (!isExternalUrl(thumbnailUrl)) {
    return thumbnailUrl;
  }

  try {
    const isVideo = thumbnailUrl.toLowerCase().endsWith('.mp4');
    console.log(`📤 Fazendo upload automático da ${isVideo ? 'vídeo' : 'thumbnail'} para Supabase...`);
    console.log('   URL original:', thumbnailUrl);
    console.log('   Card ID:', cardId);
    
    // Baixar mídia (imagem ou vídeo)
    console.log(`   ⬇️  Baixando ${isVideo ? 'vídeo' : 'imagem'}...`);
    const mediaBlob = await downloadMedia(thumbnailUrl);
    console.log(`   ✅ ${isVideo ? 'Vídeo' : 'Imagem'} baixada. Tamanho:`, (mediaBlob.size / 1024).toFixed(2), 'KB');
    
    // Detectar tipo MIME
    let mimeType = detectMimeType(mediaBlob, thumbnailUrl);
    console.log('   📋 Tipo MIME detectado:', mimeType);
    
    // Gerar nome do arquivo
    const fileName = generateFileName(cardId, thumbnailUrl);
    console.log('   📁 Nome do arquivo:', fileName);
    
    // Garantir que o tipo MIME é válido (nunca application/json)
    if (!mimeType || mimeType === 'application/json') {
      // Se for vídeo, usar video/mp4, senão image/jpeg
      if (isVideo) {
        console.warn('   ⚠️ Tipo MIME inválido detectado para vídeo:', mimeType, '- Forçando video/mp4');
        mimeType = 'video/mp4';
      } else {
        console.warn('   ⚠️ Tipo MIME inválido detectado:', mimeType, '- Forçando image/jpeg');
        mimeType = 'image/jpeg';
      }
    }

    // Verificar se o blob é válido
    if (!mediaBlob || mediaBlob.size === 0) {
      throw new Error(`Blob de ${isVideo ? 'vídeo' : 'imagem'} inválido ou vazio`);
    }

    // SOLUÇÃO: Converter Blob para ArrayBuffer para evitar serialização como JSON
    // O Supabase SDK pode estar serializando Blob como JSON, então vamos usar ArrayBuffer
    console.log('   🔄 Convertendo Blob para ArrayBuffer...');
    const arrayBuffer = await mediaBlob.arrayBuffer();
    console.log('   ✅ ArrayBuffer criado. Tamanho:', (arrayBuffer.byteLength / 1024).toFixed(2), 'KB');
    
    // Criar um novo Blob a partir do ArrayBuffer com o tipo MIME correto
    const typedBlob = new Blob([arrayBuffer], { type: mimeType });
    console.log('   📦 Blob final - Tipo:', typedBlob.type, 'Tamanho:', (typedBlob.size / 1024).toFixed(2), 'KB');
    console.log('   📋 Content-Type que será enviado:', mimeType);

    // Fazer upload para o Supabase Storage
    // Usar API REST diretamente para evitar problemas de Content-Type do SDK
    console.log('   📤 Fazendo upload para Supabase Storage via API REST...');
    let uploadData: any = null;
    let uploadError: any = null;

    try {
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
      
      // Obter token de autenticação
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || supabaseKey;
      
      // Usar API REST diretamente com controle total dos headers
      const uploadUrl = `${SUPABASE_URL}/storage/v1/object/${BUCKET_NAME}/${fileName}`;
      
      console.log('   🔧 Usando API REST com headers controlados...');
      console.log('   📋 Content-Type único:', mimeType);
      console.log('   📦 Tamanho do arquivo:', (arrayBuffer.byteLength / 1024).toFixed(2), 'KB');
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': mimeType, // Apenas um Content-Type, sem vírgulas
          'x-upsert': 'true',
          'Cache-Control': '3600',
          'apikey': supabaseKey
        },
        body: arrayBuffer // Enviar ArrayBuffer diretamente (não JSON)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API REST falhou: ${response.status} - ${errorText}`);
      }

      // A resposta pode ser vazia ou JSON dependendo da configuração
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        uploadData = await response.json();
      } else {
        // Se não for JSON, criar objeto com o path
        uploadData = { path: fileName };
      }
      
      uploadError = null;
      console.log('   ✅ Upload via API REST bem-sucedido!');
    } catch (restError: any) {
      console.error('❌ Erro ao fazer upload via API REST:', restError);
      uploadError = restError;
    }

    if (uploadError) {
      console.error('❌ Erro ao fazer upload:', uploadError);
      console.error('   Detalhes:', JSON.stringify(uploadError, null, 2));
      // Se falhar, retornar a URL original
      return thumbnailUrl;
    }

    console.log('   ✅ Upload concluído:', uploadData);

    // Obter URL pública
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(fileName);

    console.log(`✅ ${isVideo ? 'Vídeo' : 'Thumbnail'} migrada com sucesso!`);
    console.log('   URL original:', thumbnailUrl);
    console.log('   URL Supabase:', publicUrl);
    return publicUrl;
  } catch (error: any) {
    console.error(`❌ Erro ao migrar ${thumbnailUrl.toLowerCase().endsWith('.mp4') ? 'vídeo' : 'thumbnail'}:`, error);
    console.error('   Tipo:', error?.constructor?.name);
    console.error('   Mensagem:', error?.message);
    console.error('   Stack:', error?.stack);
    // Em caso de erro, retornar a URL original para não bloquear o cadastro
    return thumbnailUrl;
  }
}

